import React, { useContext, useEffect } from 'react';
import { useFormik } from 'formik';

import type { PasswordFormValues } from '../../../types';

import { URL } from 'app/api/constants';
import { FormControl } from 'app/design-system/components/FormControl';
import { PASSWORD_MIN_LENGTH } from 'app/design-system/components/Input/constants';
import PasswordInput from 'app/design-system/components/Input/PasswordInput';
import { useTranslation } from 'app/lang';

import { sendRequest } from '../../../actions';
import { CHECK_TIME_DELAY, PASSWORD_FORM_VALUES } from '../../../constants';
import { StepContext } from '../../../contexts/stepContext';
import { UserContext } from '../../../contexts/userContext';
import { getValidatePassword } from './PasswordForm.validations';

import dictionary from './PasswordForm.dictionary.json';

import styles from './PasswordForm.module.scss';

export const PasswordForm = (): JSX.Element => {
  const { t } = useTranslation();
  const { userData: { token, requisite }, setUserData } = useContext(UserContext);
  const { activeStep, forwardStep } = useContext(StepContext);
  const validate = getValidatePassword(t);
  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues: PASSWORD_FORM_VALUES,
    validate,
    onSubmit: (values: PasswordFormValues, formikHelpers) => {
      const { newPass: password } = values;

      setUserData((prevState) => ({ ...prevState, disabled: true }));

      sendRequest(URL.PASSWORD_RESET_SEND_NEW_PASS, { password, token })
        .then(() => {
          setUserData((prevState) => ({ ...prevState, checked: true }));

          setTimeout(() => {
            setUserData((prevState) => ({ ...prevState, checked: false }));
            forwardStep(activeStep);
          }, CHECK_TIME_DELAY);
        })
        .catch(() => {
          formikHelpers.setSubmitting(false);
          formikHelpers.setFieldError('newPass', t(dictionary.passwordRequired, { count: PASSWORD_MIN_LENGTH }));
        })
        .finally(() => setUserData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    setFieldValue('login', requisite);
  }, []);

  const newPasswordError = touched.newPass && errors.newPass;

  return (
    <form id={activeStep.toString()}
          onSubmit={handleSubmit}
          className={styles.newPassInputs}>
      <FormControl label={t(dictionary.newPassLabel)}
                   className={styles.passwordFormControl}
                   error={newPasswordError}
                   data-test="Modal-restorePassword-newPassLabel">
        <PasswordInput name="newPass"
                       value={values.newPass}
                       error={!!newPasswordError}
                       placeholder={t(dictionary.newPassPlaceholder)}
                       onChange={handleChange}
                       disabled={isSubmitting}
                       className={styles.passwordInput}
                       data-test="Modal-restorePassword-newPassInput" />
        <span className={styles.validationInfo}>
          {t(dictionary.validationInfo)}
        </span>
      </FormControl>
    </form>
  );
};
